import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { toast } from "sonner"

export interface FinancialRevenue {
  id: string
  condominium_id: string
  description: string
  amount_cents: number
  currency: string
  category: string
  revenue_date: string
  unit_id?: string
  payment_method?: string
  notes?: string
  receipt_url?: string
  created_by?: string
  created_at: string
  updated_at: string
  unit?: {
    number: string
    building?: string
  }
}

export const useFinancialRevenues = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["financial-revenues", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []

      const { data, error } = await supabase
        .from("financial_revenues")
        .select(`
          *,
          unit:units(number, building)
        `)
        .eq("condominium_id", profile.condominium_id)
        .order("revenue_date", { ascending: false })

      if (error) throw error
      return data as FinancialRevenue[]
    },
    enabled: !!profile?.condominium_id
  })
}

export const useCreateFinancialRevenue = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (revenueData: Omit<FinancialRevenue, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'condominium_id' | 'unit'>) => {
      if (!profile?.id || !profile?.condominium_id) {
        throw new Error("Usuário não autenticado")
      }

      const { data, error } = await supabase
        .from("financial_revenues")
        .insert({
          ...revenueData,
          created_by: profile.id,
          condominium_id: profile.condominium_id
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-revenues"] })
      toast.success("Receita registrada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao registrar receita:", error)
      toast.error("Erro ao registrar receita")
    }
  })
}

export const useUpdateFinancialRevenue = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinancialRevenue> }) => {
      const { data, error } = await supabase
        .from("financial_revenues")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-revenues"] })
      toast.success("Receita atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar receita:", error)
      toast.error("Erro ao atualizar receita")
    }
  })
}

export const useDeleteFinancialRevenue = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("financial_revenues")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-revenues"] })
      toast.success("Receita removida com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao remover receita:", error)
      toast.error("Erro ao remover receita")
    }
  })
}

export const useRevenueStats = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["revenue-stats", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return null

      const { data, error } = await supabase
        .from("financial_revenues")
        .select("category, amount_cents")
        .eq("condominium_id", profile.condominium_id)

      if (error) throw error

      const stats = data.reduce((acc, revenue) => {
        acc.total_amount += revenue.amount_cents
        acc.by_category[revenue.category] = (acc.by_category[revenue.category] || 0) + revenue.amount_cents
        return acc
      }, {
        total_amount: 0,
        by_category: {} as Record<string, number>
      })

      return stats
    },
    enabled: !!profile?.condominium_id
  })
}
